import type { CityConfig } from "@/pages/city-template/types";

export const bloomsburgConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Bloomsburg",
  state: "Pennsylvania",
  stateAbbr: "PA",
  slug: "bloomsburg",
  route: "/cities/bloomsburg",
  title: "Emergency Passport Bloomsburg, PA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Bloomsburg, PA — as fast as 24 hours. Same-day courier processing available for Columbia County travelers, Bloomsburg University students, and Central Pennsylvania residents. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Bloomsburg, Pennsylvania",
  heroSubtitle: "Studying abroad through Bloomsburg University and your passport just expired? Family trip to Canada from Central PA and realized the kids need passports? Fast Passport Center helps Bloomsburg residents get their passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Bloomsburg%20Pennsylvania%20historic%20downtown%20Main%20Street%20with%20classic%20brick%20storefronts%20and%20tree-lined%20sidewalks%2C%20Bloomsburg%20University%20campus%20visible%20in%20distance%2C%20Susquehanna%20River%20valley%20landscape%20with%20rolling%20green%20hills%2C%20warm%20autumn%20golden%20hour%20lighting%2C%20small%20college%20town%20atmosphere%2C%20vibrant%20seasonal%20colors%2C%20photorealistic%20aerial%20neighborhood%20photography%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=bloomsburg-hero-01&orientation=landscape",
  heroImageAlt: "Bloomsburg Pennsylvania historic downtown with Bloomsburg University and Susquehanna River valley",
  geoPosition: "41.0037;-76.4564",
  geoPlacename: "Bloomsburg, Pennsylvania",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Philadelphia Passport Agency",
    address: "200 Chestnut St, Philadelphia, PA 19106",
    distance: "About 140 miles southeast — 2.5 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Bloomsburg", zips: "17815" },
    { name: "Berwick", zips: "18603" },
    { name: "Danville", zips: "17821" },
    { name: "Catawissa", zips: "17820" },
    { name: "Mifflinville", zips: "18631" },
    { name: "Orangeville", zips: "17859" },
    { name: "Millville", zips: "17846" },
    { name: "Benton", zips: "17814" },
    { name: "Lightstreet", zips: "17839" },
    { name: "Espy", zips: "17815" },
    { name: "Buckhorn", zips: "17815" },
    { name: "Nescopeck", zips: "18635" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home in Bloomsburg or your dorm at Bloomsburg University in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Columbia County", desc: "FedEx and UPS locations throughout Bloomsburg and the Columbia County area offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays — critical for Bloomsburg University students with tight study abroad deadlines." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the Philadelphia Passport Agency — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Jennifer K.",
      location: "Bloomsburg, PA",
      rating: 5,
      text: "My daughter needed a passport for a Bloomsburg University study abroad program in Spain. We had about three weeks and were panicking. Fast Passport Center walked us through every step. Her passport arrived in 8 days. Incredible service.",
      service: "New Passport",
    },
    {
      name: "Robert T.",
      location: "Berwick, PA",
      rating: 5,
      text: "Business trip to Toronto came up on short notice and my passport was expired. Fast Passport Center got it renewed in 5 days. I shipped from the FedEx in Bloomsburg and had it back before my meeting. Saved my trip.",
      service: "Passport Renewal",
    },
    {
      name: "Maria S.",
      location: "Danville, PA",
      rating: 5,
      text: "We planned a family cruise to the Bahamas and realized both kids needed passports. Fast Passport Center made the child passport process simple and clear. Both passports arrived in 9 days. We'll use them again for sure.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "bloomsburg-post",
      name: "Bloomsburg Post Office",
      type: "USPS Passport Acceptance",
      address: "230 Market St",
      city: "Bloomsburg",
      state: "PA",
      zip: "17815",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Downtown Bloomsburg",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Bloomsburg. Appointment required — book online well in advance. Photo services available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "berwick-post",
      name: "Berwick Post Office",
      type: "USPS Passport Acceptance",
      address: "513 E 2nd St",
      city: "Berwick",
      state: "PA",
      zip: "18603",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Berwick — about 9 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Berwick option for eastern Columbia County and Luzerne County border residents.",
      icon: "ri-mail-line",
    },
    {
      id: "danville-post",
      name: "Danville Post Office",
      type: "USPS Passport Acceptance",
      address: "400 Mill St",
      city: "Danville",
      state: "PA",
      zip: "17821",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Danville — about 11 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Danville option for western Columbia County and Montour County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "columbia-county-courthouse",
      name: "Columbia County Courthouse",
      type: "County Passport Acceptance",
      address: "35 W Main St",
      city: "Bloomsburg",
      state: "PA",
      zip: "17815",
      phone: "570-389-5600",
      hours: "Mon–Fri 8:30am–4:30pm",
      distance: "Downtown Bloomsburg",
      website: "https://www.columbiapa.org/",
      websiteLabel: "Columbia County Website",
      notes: "County courthouse passport acceptance. Call ahead to confirm hours and appointment requirements.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Bloomsburg before international travel?",
      a: "Yes. Through Fast Passport Center, Bloomsburg travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today.",
    },
    {
      q: "How fast can I renew my passport in Bloomsburg?",
      a: "Fast passport renewal in Bloomsburg is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years.",
    },
    {
      q: "Can Bloomsburg University students get expedited passports for study abroad?",
      a: "Absolutely. We help Bloomsburg University and Commonwealth University students get passports fast for study abroad programs. Whether it's a first-time passport or a renewal, we guide you through the process and can meet even tight semester deadlines. Start early — don't wait until the last week before your program.",
    },
    {
      q: "Does Bloomsburg have a regional passport agency?",
      a: "Bloomsburg does not have a regional passport agency. The closest is the Philadelphia Passport Agency (about 140 miles southeast). Both require appointments and proof of travel within 14 days. Fast Passport Center is the faster, more convenient solution for most Central Pennsylvania residents.",
    },
    {
      q: "Can I expedite a child passport in Bloomsburg?",
      a: "Yes. Child passport expediting in Bloomsburg is possible through Fast Passport Center. Child passports for minors 15 and under require an in-person acceptance step with parental consent. We provide a clear checklist. Once the acceptance step is complete, we handle expedited processing.",
    },
    {
      q: "What airports do Bloomsburg travelers typically use?",
      a: "Wilkes-Barre/Scranton International Airport (AVP — about 50 miles northeast) and Harrisburg International Airport (MDT — about 70 miles southwest) are the primary airports for Bloomsburg travelers. Both offer domestic connections for international flights. Philadelphia International (PHL) is about 140 miles away for direct international departures.",
    },
    {
      q: "How long does a standard passport take in Bloomsburg?",
      a: "If you apply through a standard post office acceptance facility in Bloomsburg, processing times run 6–13 weeks — sometimes longer during peak travel season. If your travel is sooner, you need expedited processing through Fast Passport Center.",
    },
    {
      q: "Can I get a passport renewal in Bloomsburg without visiting an office?",
      a: "Yes. Most Bloomsburg-area adults renewing a passport can do so entirely by mail — no in-person visit required. Start your order online, complete documents at home, and ship overnight from any FedEx or UPS location in Bloomsburg or the Columbia County area.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Bloomsburg", address: "1000 Market St, Bloomsburg, PA 17815", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/" },
    { name: "CVS — Bloomsburg", address: "225 Columbia Mall Dr, Bloomsburg, PA 17815", hours: "Daily 8 AM – 9 PM", website: "https://www.cvs.com/" },
    { name: "Walmart — Buckhorn", address: "100 Lunger Dr, Bloomsburg, PA 17815", hours: "Daily 6 AM – 11 PM", website: "https://www.walmart.com/" },
    { name: "The UPS Store — Bloomsburg", address: "265 Columbia Mall Dr, Bloomsburg, PA 17815", hours: "Mon–Fri 8 AM – 7 PM, Sat 9 AM – 3 PM", website: "https://www.theupsstore.com/" },
    { name: "FedEx Office — Wilkes-Barre", address: "253 Wyoming Ave, Kingston, PA 18704", hours: "Mon–Fri 7 AM – 10 PM", website: "https://www.fedex.com/" },
  ],
  dmvLocations: [
    {
      name: "Bloomsburg PennDOT Driver License Center",
      address: "1000 Market St, Bloomsburg, PA 17815",
      hours: "Tue–Sat 8:30 AM – 4:15 PM",
      note: "Bloomsburg PennDOT center. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Wilkes-Barre PennDOT Driver License Center",
      address: "1085 Hanover St, Wilkes-Barre, PA 18706",
      hours: "Tue–Sat 8:30 AM – 4:15 PM",
      note: "Wilkes-Barre PennDOT option for eastern Columbia County residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "AVP",
      name: "Wilkes-Barre/Scranton International Airport",
      distance: "About 50 miles northeast — 1 hour drive",
      address: "100 Terminal Rd, Avoca, PA 18641",
      note: "AVP is the closest commercial airport for Bloomsburg travelers, offering connections to major hubs for international travel. Passport required for all international connections.",
      flightUrl: "https://www.flyavp.com/",
      flightLabel: "AVP Airport Website",
      featured: true,
    },
    {
      code: "MDT",
      name: "Harrisburg International Airport",
      distance: "About 70 miles southwest — 1.25 hours drive",
      address: "1 Terminal Dr, Middletown, PA 17057",
      note: "MDT offers additional domestic connections and is a convenient option for Bloomsburg travelers heading south. Passport required for international itineraries.",
      flightUrl: "https://www.flyhia.com/",
      flightLabel: "MDT Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from AVP or MDT in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Bloomsburg and Central Pennsylvania.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Bloomsburg and Columbia County travelers renew fast — no office visit required for most renewals.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Bloomsburg", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Bloomsburg travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Bloomsburg families — minors under 16 require in-person acceptance and parental consent.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Bloomsburg residents and Bloomsburg University students.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all expedited.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Bloomsburg, Pennsylvania travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Bloomsburg and Columbia County residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Bloomsburg, PA residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Bloomsburg travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Bloomsburg families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving Bloomsburg, Pennsylvania." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Bloomsburg travelers often assume 6–8 weeks is safe — it's not. Government processing has stretched to 10–14+ weeks during peak travel season. Apply as soon as you book your trip." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs and catch these issues before submission." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. We tell you exactly what you need." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing out of Bloomsburg runs 6–13 weeks. If your trip is in under 10 weeks, standard processing is a gamble. Use our courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Bloomsburg travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Bloomsburg", description: "Drop off at any FedEx or UPS in Bloomsburg or the Columbia County area. Most locations are open evenings." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins", "Life-or-death emergencies only", "Must travel to Philadelphia (2.5 hours)", "Expect to wait all day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in Philadelphia", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Philadelphia (2.5 hours each way)", fpc: "Ship documents overnight from Bloomsburg" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Door-to-Door", agency: "You drive 2.5 hours and wait all day", fpc: "Ship from local FedEx/UPS — we handle the rest" },
    { feature: "Predictability", agency: "Appointments can be cancelled", fpc: "Predictable processing based on option" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
    { feature: "Success Rate", agency: "Depends on appointment availability", fpc: "99%+ application approval rate" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Wilkes-Barre", route: "/cities/wilkes-barre" },
    { name: "Fast passport renewal in Scranton", route: "/cities/scranton" },
    { name: "Emergency passport help near Danville", route: "/cities/danville" },
    { name: "Rush passport service for Columbia County travelers", route: "/states/pennsylvania" },
  ],
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Bloomsburg or the Columbia County area — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Bloomsburg and all of Columbia County remotely",
  serviceAreaIntro: "Whether you are in downtown Bloomsburg, Berwick, Danville, Catawissa, or anywhere in Columbia County, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Bloomsburg and Columbia County zip codes. Anywhere in the Central Susquehanna Valley — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Bloomsburg as simple as possible. Most Bloomsburg clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Bloomsburg travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from Bloomsburg, Berwick, and Danville travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Bloomsburg and Columbia County travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/pennsylvania",
  statePageName: "Pennsylvania",
  dmvWebsite: "https://www.dmv.pa.gov/",
  dmvLabel: "PennDOT — Official Website",
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from Pennsylvania Department of Health Vital Records for Columbia County.", url: "https://www.health.pa.gov/topics/certificates/Pages/Birth-Certificates.aspx" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through Pennsylvania Department of Health Vital Records.", url: "https://www.health.pa.gov/topics/certificates/Pages/Death-Certificates.aspx" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Columbia County Register of Wills.", url: "https://www.columbiapa.org/" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through Columbia County Prothonotary.", url: "https://www.columbiapa.org/" },
    { icon: "ri-global-line", label: "VitalChek", desc: "Order Pennsylvania birth, death, marriage, and divorce certificates online through VitalChek for convenience.", url: "https://www.vitalchek.com/" },
  ],
};